import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { UniversalPage, Card, Button, Input } from '@afrilink/ui';
import { salesService } from '@/domains/sales/services/salesService';

/**
 * SalesImportPage — CSV upload with POPIA certification + per-row preview.
 *
 * Expected CSV columns (header row required):
 *   full_name, email, phone, company_name, lead_source, consent_basis,
 *   consent_source, consent_at, initial_note
 *
 * lead_source ∈ csv_import / web_form / referral / cold_list / partner / walk_in / inbound_call
 * consent_basis ∈ opt_in_form / existing_customer / referral / walk_in / legitimate_interest
 * consent_at — ISO 8601 date string
 *
 * Per SP-17: the EF rejects any row missing consent_basis/consent_source/consent_at.
 * Per the POPIA certification: the manager MUST tick a checkbox attesting to consent.
 */

interface ParsedRow {
  full_name: string;
  email?: string;
  phone?: string;
  company_name?: string;
  lead_source: string;
  consent_basis: string;
  consent_source: string;
  consent_at: string;
  initial_note?: string;
}

interface ImportResult {
  accepted: number;
  skipped_duplicate: number;
  errored: number;
  rows: Array<{ row_index: number; status: string; customer_id?: string; reason?: string }>;
}

export default function SalesImportPage() {
  const [rawCsv, setRawCsv] = useState('');
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [certified, setCertified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adminCompanyId, setAdminCompanyId] = useState('');

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRawCsv(String(ev.target?.result ?? ''));
    reader.readAsText(file);
  }

  function parseCsv() {
    setParseError(null);
    setParsed(null);
    try {
      const rows = parseCsvText(rawCsv);
      setParsed(rows);
    } catch (e) {
      setParseError((e as Error).message);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!parsed || !certified) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await salesService.batchImport(parsed, true, adminCompanyId || undefined);
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UniversalPage title="Import Leads" description="CSV → leads (POPIA-enforcing)">
      {!result && (
        <form onSubmit={onSubmit} className="space-y-4">
          <Card className="p-6">
            <h2 className="text-base font-semibold text-navy-primary">Step 1 — Upload CSV</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Header required: <code className="text-xs">full_name, email, phone, company_name, lead_source, consent_basis, consent_source, consent_at, initial_note</code>
            </p>
            <Input type="file" accept=".csv" onChange={onFile} className="mt-3" />
            {rawCsv && (
              <div className="mt-3 space-y-2">
                <textarea
                  value={rawCsv}
                  onChange={(e) => setRawCsv(e.target.value)}
                  rows={6}
                  className="w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
                />
                <Button type="button" variant="outline" onClick={parseCsv}>Parse</Button>
                {parseError && <div className="rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">{parseError}</div>}
              </div>
            )}
          </Card>

          {parsed && (
            <Card className="p-6">
              <h2 className="text-base font-semibold text-navy-primary">Step 2 — Preview ({parsed.length} rows)</h2>
              <div className="mt-3 max-h-64 overflow-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr><th className="px-2 py-1 text-left">Name</th><th className="px-2 py-1 text-left">Email</th><th className="px-2 py-1 text-left">Phone</th><th className="px-2 py-1 text-left">Source</th><th className="px-2 py-1 text-left">Consent</th></tr>
                  </thead>
                  <tbody>
                    {parsed.slice(0, 100).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1">{r.full_name}</td>
                        <td className="px-2 py-1">{r.email ?? '—'}</td>
                        <td className="px-2 py-1">{r.phone ?? '—'}</td>
                        <td className="px-2 py-1">{r.lead_source}</td>
                        <td className="px-2 py-1">{r.consent_basis}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.length > 100 && <p className="p-2 text-center text-xs text-muted-foreground">…{parsed.length - 100} more rows</p>}
              </div>
            </Card>
          )}

          {parsed && (
            <Card className="p-6">
              <h2 className="text-base font-semibold text-navy-primary">Step 3 — POPIA Certification</h2>
              <label className="mt-3 flex items-start gap-2 text-sm">
                <input type="checkbox" checked={certified} onChange={(e) => setCertified(e.target.checked)} className="mt-1" />
                <span>
                  I certify that <strong>every row</strong> in this batch has documented consent matching the declared <code>consent_basis</code>, and I have evidence on file (signed forms, opt-in logs, etc.) available for inspection. This certification is recorded in the audit log against my account.
                </span>
              </label>
              <Input
                value={adminCompanyId}
                onChange={(e) => setAdminCompanyId(e.target.value)}
                placeholder="originating_company_id (UUID — admin only; leave blank otherwise)"
                className="mt-3"
              />
              {error && <div className="mt-3 rounded-md border border-danger/30 bg-danger/5 p-2 text-sm text-danger">{error}</div>}
              <Button type="submit" variant="primary" disabled={!certified || submitting} className="mt-4">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Import {parsed.length} leads
              </Button>
            </Card>
          )}
        </form>
      )}

      {result && (
        <Card className="p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-navy-primary">
            <CheckCircle2 className="h-5 w-5 text-success" /> Import complete
          </h2>
          <dl className="mt-4 grid grid-cols-3 gap-4 text-center">
            <Stat label="Accepted" value={result.accepted} tone="success" />
            <Stat label="Duplicates skipped" value={result.skipped_duplicate} tone="warn" />
            <Stat label="Errored" value={result.errored} tone="danger" />
          </dl>
          {result.errored > 0 && (
            <div className="mt-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-danger">
                <AlertTriangle className="h-4 w-4" /> Errored rows
              </h3>
              <ul className="mt-2 space-y-1 text-xs">
                {result.rows.filter((r) => r.status === 'errored').slice(0, 50).map((r) => (
                  <li key={r.row_index}>Row {r.row_index + 1}: {r.reason}</li>
                ))}
              </ul>
            </div>
          )}
          <Button onClick={() => { setResult(null); setParsed(null); setRawCsv(''); setCertified(false); }} variant="outline" className="mt-6">Import another batch</Button>
        </Card>
      )}
    </UniversalPage>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'success' | 'warn' | 'danger' }) {
  const color = tone === 'success' ? 'text-success' : tone === 'warn' ? 'text-warning-dark' : 'text-danger';
  return (
    <div>
      <div className={`font-display text-3xl font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function parseCsvText(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error('CSV must have a header row + at least one data row');
  const headers = splitCsvLine(lines[0]!).map((h) => h.trim().toLowerCase());
  const required = ['full_name', 'lead_source', 'consent_basis', 'consent_source', 'consent_at'];
  for (const r of required) {
    if (!headers.includes(r)) throw new Error(`Missing required column: ${r}`);
  }
  const idx = (col: string) => headers.indexOf(col);
  return lines.slice(1).filter((l) => l.trim().length > 0).map((line, i) => {
    const cols = splitCsvLine(line);
    return {
      full_name: cols[idx('full_name')] ?? '',
      email: cols[idx('email')] || undefined,
      phone: cols[idx('phone')] || undefined,
      company_name: cols[idx('company_name')] || undefined,
      lead_source: cols[idx('lead_source')] ?? '',
      consent_basis: cols[idx('consent_basis')] ?? '',
      consent_source: cols[idx('consent_source')] ?? '',
      consent_at: cols[idx('consent_at')] ?? '',
      initial_note: cols[idx('initial_note')] || undefined,
    };
  });
}

function splitCsvLine(line: string): string[] {
  // Minimal CSV parser — handles quoted fields containing commas.
  const result: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else { cur += c; }
    } else {
      if (c === ',') { result.push(cur); cur = ''; }
      else if (c === '"' && cur === '') { inQ = true; }
      else { cur += c; }
    }
  }
  result.push(cur);
  return result;
}
